/**
 * `AddToCartBase` — shared base class for the add-to-cart path.
 *
 * Deliberate exception to "one custom element per file": this file defines no
 * element. Extracted at the third repetition of the /cart/add.js fetch
 * (`<product-form>`, `<quick-add>`, `<quick-order>`), per repo convention.
 * Must be loaded in theme.liquid before the three files that extend it —
 * script order is the only contract.
 *
 * Subclasses implement `setStatus(message, isError)` and call `addToCart()`.
 */
class AddToCartBase extends HTMLElement {
  /**
   * Posts to /cart/add.js and drives status, events and the button state.
   * Dispatches `shopify:cart:lines-update` on `target` before the fetch, then
   * `thcabina:cart-updated` (on `this`) or `shopify:cart:error`. Shopify
   * answers add errors as JSON 200 with a `status` field — hence the throw.
   *
   * @param {object} o
   * @param {string} o.context 'product' | 'quick-add' | 'quick-order'
   * @param {Array} o.lines [{ merchandiseId, quantity }]
   * @param {string} o.body already-serialized request body
   * @param {string} [o.contentType] default 'application/x-www-form-urlencoded'
   * @param {EventTarget} [o.target] target of lines-update, default this
   * @param {string} [o.successMessage]
   * @param {HTMLButtonElement} [o.button] disabled while the fetch runs
   * @returns {Promise<boolean>} true on success
   */
  addToCart({ context, lines, body, contentType, target, successMessage, button }) {
    this.setStatus('');
    if (button) button.disabled = true;
    const result = this.deferred();

    window.THCabina.events.dispatch(
      'shopify:cart:lines-update',
      { action: 'add', context, lines, promise: result.promise },
      target || this
    );

    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': contentType || 'application/x-www-form-urlencoded' },
      body
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && data.status >= 400) {
          throw data;
        }
        this.setStatus(successMessage || '', false);
        result.resolve({ cart: null });
        window.THCabina.events.dispatch('thcabina:cart-updated', {}, this);
        return true;
      })
      .catch((error) => {
        const message = (error && (error.description || error.message)) || '';
        this.setStatus(message, true);
        result.resolve({ cart: null, userErrors: [{ message }] });
        window.THCabina.events.dispatch('shopify:cart:error', {
          error: { message }
        });
        return false;
      })
      .finally(() => {
        if (button) button.disabled = false;
      });
  }

  deferred() {
    let resolve;
    const promise = new Promise((r) => {
      resolve = r;
    });
    return { promise, resolve };
  }
}
